import React, { useState } from 'react';
import { Coupon as CouponType } from '../types';
import { Coupon } from '../Coupon';
import { styles } from './CouponsContainer.css';
import CalloutFont from '@jbhi-fi/lanyard-ui/font/CalloutFont';
import Button from '@jbhi-fi/lanyard-ui/components/Button';
import ArrowIcon from '@jbhi-fi/lanyard-ui/components/Icons/Arrow';
import {
  BIRTHDAY_MESSAGE,
  DEFAULT_MESSAGE,
  DEFAULT_CONTAINER_TITLE,
  TITLE_MINI,
  TITLE_ATTACH_SLIDEOUT,
  COUPONS_USAGE_MESSAGE,
  ACCOUNT_DROPDOWN_TITLE,
} from './constants';

type CouponsContainerProps = {
  variant?: 'mini' | 'full';
  coupons?: Array<CouponType>;
  title?:
    | typeof DEFAULT_CONTAINER_TITLE
    | typeof TITLE_MINI
    | typeof TITLE_ATTACH_SLIDEOUT
    | typeof ACCOUNT_DROPDOWN_TITLE;
  shouldShowSubheading?: boolean;
  isCloseToBirthMonth?: boolean;
  shouldDisplayStatus?: boolean;
  viewLimit?: number;
  onApplyCoupon: (couponNumber: string) => void;
  onRemoveCoupon?: (couponNumber: string) => void;
};

export const CouponsContainer = ({
  variant = 'full',
  coupons,
  title,
  shouldShowSubheading = false,
  isCloseToBirthMonth = false,
  shouldDisplayStatus = true,
  viewLimit = 2,
  onApplyCoupon,
  onRemoveCoupon,
}: CouponsContainerProps) => {
  const [shouldShowMoreCoupons, setShouldShowMoreCoupons] = useState(false);

  const calculatedViewLimit = Math.max(viewLimit, coupons?.filter(coupon => coupon.isApplied)?.length ?? 0);

  return (
    <div className={styles.couponsContainer({ variant })}>
      {title ? (
        <CalloutFont className={styles.header} as="h3">
          {title}
        </CalloutFont>
      ) : null}
      {coupons?.length ? (
        <>
          {shouldShowSubheading ? <p>{COUPONS_USAGE_MESSAGE}</p> : null}
          {coupons
            .slice()
            .sort((a, b) => {
              const aDate = a.allocatedDate ? new Date(a.allocatedDate).getTime() : 0;
              const bDate = b.allocatedDate ? new Date(b.allocatedDate).getTime() : 0;
              return bDate - aDate;
            })
            .slice(0, shouldShowMoreCoupons ? coupons.length : calculatedViewLimit)
            .map((coupon, index) => (
              <Coupon
                key={`${coupon.couponName}-${index}`}
                viewMode={variant}
                type="perks"
                name={coupon.content?.displayName || coupon.couponName}
                number={coupon.couponCode}
                expiry={coupon.expiryDate}
                isApplied={coupon.isApplied}
                isBestDeal={coupon.isBestDeal}
                success={shouldDisplayStatus ? coupon.success : true}
                onApplyCoupon={onApplyCoupon}
                onRemoveCoupon={onRemoveCoupon}
              />
            ))}
        </>
      ) : (
        <p>{isCloseToBirthMonth ? BIRTHDAY_MESSAGE : DEFAULT_MESSAGE}</p>
      )}

      {(coupons?.length ?? 0) > calculatedViewLimit && (
        <div className={styles.showMoreContainer}>
          <Button
            type="linkWithIcon"
            label={shouldShowMoreCoupons ? 'Show fewer coupons' : 'Show more coupons'}
            classes={styles.button}
            icon={<ArrowIcon className={styles.icon({ isOpen: shouldShowMoreCoupons })} />}
            onClick={() => setShouldShowMoreCoupons(!shouldShowMoreCoupons)}
          />
        </div>
      )}
    </div>
  );
};
